import { useEffect, useState } from "react";
import { Handshake, Loader2, Plus, Pencil, Power, Inbox, ExternalLink, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const ORIGIN = API.replace(/\/api$/, "");
import AdminShell from "../components/AdminShell";

const inputCls = "w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-100 outline-none focus:border-[#0A8B85]";
const labelCls = "mb-1 block text-[11px] font-semibold text-neutral-400";
const STATUS_LABEL = { pending: "Pending", introduced: "Introduced", closed: "Closed" };
const STATUS_CLS = { pending: "bg-amber-500/15 text-amber-300", introduced: "bg-emerald-500/15 text-emerald-300", closed: "bg-neutral-700 text-neutral-300" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" }) : "—");
const apiErr = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : fb; };

const toForm = (p, focus) => ({
  name: p?.name || "", blurb: p?.blurb || "", type_label: p?.type_label || "", website: p?.website || "",
  status: p?.status || "active",
  focus_areas: p ? (p.focus_areas?.length === focus.length ? [] : p.focus_areas || []) : [],
});

function PartnerForm({ tenantId, partner, focus, onSaved, onCancel }) {
  const [f, setF] = useState(() => toForm(partner, focus));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const allAreas = f.focus_areas.length === 0;
  const toggleArea = (a) => setF({ ...f, focus_areas: f.focus_areas.includes(a) ? f.focus_areas.filter((x) => x !== a) : [...f.focus_areas, a] });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = partner
        ? await api.put(`/admin/tenants/${tenantId}/partners/${partner.partner_id}`, f)
        : await api.post(`/admin/tenants/${tenantId}/partners`, f);
      toast.success(partner ? "Partner updated" : "Partner added"); onSaved(data);
    } catch (err) { toast.error(apiErr(err, "Couldn't save partner")); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-neutral-800 bg-neutral-950 p-5" data-testid="partner-form">
      <h2 className="text-base font-bold text-white" data-testid="partner-form-title">{partner ? `Edit ${partner.name}` : "New ecosystem partner"}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block"><span className={labelCls}>Partner name *</span><input required value={f.name} onChange={set("name")} className={inputCls} placeholder="e.g. Africa Climate Ventures" data-testid="partner-name" /></label>
        <label className="block"><span className={labelCls}>Type (optional)</span><input value={f.type_label} onChange={set("type_label")} className={inputCls} placeholder="VC · Accelerator · Grant funder" data-testid="partner-type" /></label>
        <label className="block sm:col-span-2"><span className={labelCls}>One-line description *</span><input required maxLength={220} value={f.blurb} onChange={set("blurb")} className={inputCls} placeholder="What they fund and for whom" data-testid="partner-blurb" /></label>
        <label className="block"><span className={labelCls}>Website (optional)</span><input type="url" value={f.website} onChange={set("website")} className={inputCls} placeholder="https://" data-testid="partner-website" /></label>
        <label className="block"><span className={labelCls}>Status</span>
          <select value={f.status} onChange={set("status")} className={inputCls} data-testid="partner-status">
            <option value="active">Active (shown on results)</option><option value="inactive">Inactive (hidden)</option>
          </select></label>
      </div>
      <div>
        <span className={labelCls}>Focus area</span>
        <div className="flex flex-wrap gap-2" data-testid="partner-focus-areas">
          <button type="button" onClick={() => setF({ ...f, focus_areas: [] })} data-testid="partner-focus-all"
            className={"rounded-full border px-3 py-1 text-xs font-semibold " + (allAreas ? "border-[#0A8B85] bg-[#0A8B85]/20 text-[#7BD3CE]" : "border-neutral-700 text-neutral-400")}>All three</button>
          {focus.map((a) => (
            <button key={a} type="button" onClick={() => toggleArea(a)} data-testid={`partner-focus-${a}`}
              className={"rounded-full border px-3 py-1 text-xs font-semibold capitalize " + (f.focus_areas.includes(a) ? "border-[#0A8B85] bg-[#0A8B85]/20 text-[#7BD3CE]" : "border-neutral-700 text-neutral-400")}>{a}</button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-neutral-700 px-3 py-2 text-xs font-semibold text-neutral-300" data-testid="partner-cancel">Cancel</button>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white hover:bg-[#087a74] disabled:opacity-60" data-testid="partner-save">{busy && <Loader2 size={13} className="animate-spin" />} {partner ? "Save changes" : "Add partner"}</button>
      </div>
    </form>
  );
}

function LogoPicker({ tenantId, partner, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/tenants/${tenantId}/partners/${partner.partner_id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged(data); toast.success("Logo uploaded");
    } catch (e) { toast.error(apiErr(e, "Couldn't upload logo")); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { const { data } = await api.delete(`/admin/tenants/${tenantId}/partners/${partner.partner_id}/logo`); onChanged(data); toast.success("Logo removed"); }
    catch { toast.error("Couldn't remove logo"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2" data-testid={`partner-logo-picker-${partner.partner_id}`}>
      <span className="flex h-12 w-20 items-center justify-center overflow-hidden rounded-lg border border-neutral-800 bg-white">
        {partner.logo_url ? <img src={`${ORIGIN}${partner.logo_url}`} alt={`${partner.name} logo`} className="max-h-10 max-w-[72px] object-contain" data-testid={`partner-logo-img-${partner.partner_id}`} />
          : <span className="text-[10px] text-neutral-400">No logo</span>}
      </span>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-neutral-700 px-2.5 py-1.5 text-[11px] font-semibold text-neutral-200 hover:bg-neutral-800">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {partner.logo_url ? "Replace" : "Upload logo"}
        <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`partner-logo-input-${partner.partner_id}`} />
      </label>
      {partner.logo_url && <button type="button" onClick={remove} disabled={busy} className="text-neutral-500 hover:text-red-300" data-testid={`partner-logo-remove-${partner.partner_id}`}><Trash2 size={13} /></button>}
    </div>
  );
}

function PartnersTab({ tenant }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);
  const load = () => api.get(`/admin/tenants/${tenant.tenant_id}/partners`).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load partners"));
  useEffect(() => { load(); }, [tenant.tenant_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const upsert = (p) => setData((d) => ({ ...d, partners: d.partners.some((x) => x.partner_id === p.partner_id) ? d.partners.map((x) => (x.partner_id === p.partner_id ? p : x)) : [p, ...d.partners] }));
  const toggle = async (p) => {
    const next = p.status === "active" ? "inactive" : "active";
    try { const { data: u } = await api.post(`/admin/tenants/${tenant.tenant_id}/partners/${p.partner_id}/status`, { status: next }); upsert(u); toast.success(next === "active" ? `${p.name} is back on the results screen` : `${p.name} hidden from results`); }
    catch { toast.error("Couldn't update status"); }
  };
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>;
  if (mode !== null) return <PartnerForm tenantId={tenant.tenant_id} partner={mode === "new" ? null : mode} focus={data.focus_sectors} onCancel={() => setMode(null)} onSaved={(p) => { upsert(p); setMode(null); }} />;
  return (
    <div className="space-y-3" data-testid="partners-tab">
      <div className="flex justify-end"><button onClick={() => setMode("new")} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white hover:bg-[#087a74]" data-testid="partner-new-btn"><Plus size={14} /> Add partner</button></div>
      {data.partners.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-800 p-12 text-center text-sm text-neutral-500" data-testid="partners-empty">No curated partners yet. Results fall back to the Clinq funder directory until you add some.</div>
      ) : (
        <ul className="space-y-2" data-testid="partners-list">
          {data.partners.map((p) => (
            <li key={p.partner_id} className={"flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-neutral-800 bg-neutral-950 p-4 " + (p.status !== "active" ? "opacity-60" : "")} data-testid={`partner-row-${p.partner_id}`}>
              <div className="flex min-w-0 gap-4">
                <LogoPicker tenantId={tenant.tenant_id} partner={p} onChanged={upsert} />
                <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">{p.name}
                  <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] font-semibold text-neutral-300">{p.type_label}</span>
                  <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase " + (p.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-neutral-700 text-neutral-300")} data-testid={`partner-status-${p.partner_id}`}>{p.status}</span>
                  {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="text-[#7BD3CE]"><ExternalLink size={11} /></a>}
                </p>
                <p className="mt-1 text-xs text-neutral-400">{p.blurb}</p>
                <p className="mt-1 text-[11px] capitalize text-neutral-500">Focus: {(p.focus_areas || []).join(", ")}</p>
                </div>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <button onClick={() => setMode(p)} className="inline-flex items-center gap-1 rounded-lg border border-neutral-700 px-2.5 py-1.5 text-[11px] font-semibold text-neutral-200 hover:bg-neutral-800" data-testid={`partner-edit-${p.partner_id}`}><Pencil size={12} /> Edit</button>
                <button onClick={() => toggle(p)} className={"inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold " + (p.status === "active" ? "bg-red-500/10 text-red-300 hover:bg-red-500/20" : "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20")} data-testid={`partner-toggle-${p.partner_id}`}><Power size={12} /> {p.status === "active" ? "Deactivate" : "Reactivate"}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RequestsTab({ tenant }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/tenants/${tenant.tenant_id}/connection-requests`).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load requests")); }, [tenant.tenant_id]);
  const setStatus = async (r, status) => {
    try { const { data: u } = await api.put(`/admin/connection-requests/${r.request_id}/status`, { status }); setData((d) => ({ ...d, requests: d.requests.map((x) => (x.request_id === u.request_id ? u : x)) })); toast.success(`Marked ${STATUS_LABEL[status]}`); }
    catch { toast.error("Couldn't update status"); }
  };
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>;
  if (!data.requests.length) return <div className="rounded-2xl border border-dashed border-neutral-800 p-12 text-center text-sm text-neutral-500" data-testid="requests-empty">No connection requests yet.</div>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-neutral-800" data-testid="requests-tab">
      <table className="w-full text-left text-xs">
        <thead className="bg-neutral-900 text-[11px] uppercase tracking-wide text-neutral-400">
          <tr><th className="px-4 py-3">Business</th><th className="px-4 py-3">Founder</th><th className="px-4 py-3">Partner requested</th><th className="px-4 py-3">Requested</th><th className="px-4 py-3">Status</th></tr>
        </thead>
        <tbody className="divide-y divide-neutral-800 bg-neutral-950 text-neutral-200">
          {data.requests.map((r) => (
            <tr key={r.request_id} data-testid={`request-row-${r.request_id}`}>
              <td className="px-4 py-3 font-semibold text-white">{r.business_name}<p className="text-[10px] font-normal text-neutral-500">Score {r.score ?? "—"} · {(r.doc_types || []).length} doc type{(r.doc_types || []).length === 1 ? "" : "s"}</p></td>
              <td className="px-4 py-3">{r.founder_name}<p className="text-[10px] text-neutral-500">{r.founder_email}</p></td>
              <td className="px-4 py-3">{r.partner_name}<p className="text-[10px] text-neutral-500">{r.partner_type}</p></td>
              <td className="px-4 py-3 whitespace-nowrap">{fmt(r.requested_at)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase " + STATUS_CLS[r.status]} data-testid={`request-status-${r.request_id}`}>{STATUS_LABEL[r.status]}</span>
                  <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} className="rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1 text-[11px] text-neutral-100" data-testid={`request-status-select-${r.request_id}`}>
                    {data.statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPartners() {
  const [tenants, setTenants] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState("partners");
  useEffect(() => {
    api.get("/admin/tenants").then((r) => {
      const eco = r.data.tenants.filter((t) => t.partner_type === "ecosystem");
      setTenants(eco); setTenant(eco.find((t) => t.subdomain === "fsd") || eco[0] || null);
    }).catch(() => toast.error("Couldn't load tenants"));
  }, []);
  return (
    <AdminShell current="/admin/partners">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3" data-testid="admin-partners-page">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A8B85]/15 text-[#7BD3CE]"><Handshake className="h-5 w-5" /></span>
          <div>
            <h1 className="text-xl font-extrabold text-white">{tenant ? `${tenant.name} partners` : "Ecosystem partners"}</h1>
            <p className="text-sm text-neutral-400">Curated partners shown on the {tenant?.hostname || "ecosystem"} results screen, and the introductions founders have asked for.</p>
          </div>
        </div>
        {tenants && tenants.length > 1 && (
          <select value={tenant?.tenant_id || ""} onChange={(e) => setTenant(tenants.find((t) => t.tenant_id === e.target.value))} className={inputCls + " w-auto"} data-testid="partners-tenant-select">
            {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
          </select>
        )}
      </div>
      {!tenants ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>
        : !tenant ? <div className="rounded-2xl border border-dashed border-neutral-800 p-12 text-center text-sm text-neutral-500">No ecosystem-partner tenants yet. Create one under Tenants with partner type “Ecosystem partner”.</div>
        : (
          <>
            <div className="mb-4 flex gap-1 rounded-xl bg-neutral-900 p-1 text-xs font-bold" data-testid="partners-tabs">
              {[["partners", "Partners", Handshake], ["requests", "Connection requests", Inbox]].map(([k, label, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`partners-tab-${k}`} className={"inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 " + (tab === k ? "bg-neutral-800 text-white" : "text-neutral-400 hover:text-neutral-200")}><Icon size={13} /> {label}</button>
              ))}
            </div>
            {tab === "partners" ? <PartnersTab key={tenant.tenant_id} tenant={tenant} /> : <RequestsTab key={tenant.tenant_id} tenant={tenant} />}
          </>
        )}
    </AdminShell>
  );
}
